/**
 * Workspace dashboard data normalization — defense-in-depth safety layer.
 *
 * This is the SECOND line of defense. The FIRST line is the API client
 * (api.ts) which validates response shapes and logs contract violations.
 * This normalizer catches anything the API client missed.
 *
 * TODO: Phase 3 — replace this normalizer with backend DTO validation
 * (class-validator decorators on response DTOs) so the API contract is
 * enforced at the source. This file becomes a thin passthrough once
 * the backend guarantees response shapes via DTOs.
 *
 * Rules:
 * - Arrays are ALWAYS arrays (never null, never undefined)
 * - Numbers are ALWAYS numbers (never NaN, never undefined)
 * - Objects are ALWAYS objects (never null)
 * - Strings are ALWAYS strings (never null, never undefined)
 */

import type {
  DashboardSummary,
  DashboardMilestone,
  DashboardRisksResponse,
  WorkspaceHealthData,
  WorkspaceSummary,
} from '../api';

/* ── Safe accessors ──────────────────────────────────────────── */

function safeArray<T>(val: unknown): T[] {
  return Array.isArray(val) ? val : [];
}

function safeNumber(val: unknown, fallback = 0): number {
  if (typeof val === 'number' && Number.isFinite(val)) return val;
  return fallback;
}

function safeObject<T extends Record<string, unknown>>(val: unknown): T {
  return (val && typeof val === 'object' && !Array.isArray(val) ? val : {}) as T;
}

/* ── Normalizers ─────────────────────────────────────────────── */

export function normalizeDashboardSummary(
  raw: DashboardSummary | null | undefined,
): DashboardSummary {
  const d = safeObject<any>(raw);
  const documentsSummary = safeObject<{ total?: unknown }>(d.documentsSummary);
  return {
    projectCount:
      typeof d.projectCount === 'number' && Number.isFinite(d.projectCount)
        ? d.projectCount
        : undefined,
    projectStatusSummary: safeObject(d.projectStatusSummary),
    projectStateSummary:
      d.projectStateSummary &&
      typeof d.projectStateSummary === 'object' &&
      !Array.isArray(d.projectStateSummary)
        ? (d.projectStateSummary as Record<string, number>)
        : undefined,
    openRiskCount: safeNumber(d.openRiskCount),
    documentsSummary: {
      total: safeNumber(documentsSummary.total),
    },
    upcomingMilestonesCount: safeNumber(d.upcomingMilestonesCount),
  };
}

export function normalizeMilestones(
  raw: DashboardMilestone[] | null | undefined,
): DashboardMilestone[] {
  return safeArray<DashboardMilestone>(raw);
}

export function normalizeRisks(
  raw: DashboardRisksResponse | null | undefined,
): DashboardRisksResponse {
  const d = safeObject<any>(raw);
  // RISK-COUNT-FE-1 — do not invent 0 when count is missing; OpenRisksCard
  // treats a non-finite count as Unavailable (never a false empty).
  const count =
    typeof d.count === 'number' && Number.isFinite(d.count) ? d.count : Number.NaN;
  return {
    count,
    items: safeArray(d.items),
  } as DashboardRisksResponse;
}

/** Copy every finite count. Absent fields stay absent — do not invent 0. */
function finiteCounts(raw: unknown): Record<string, number> {
  const src = safeObject<Record<string, unknown>>(raw);
  const out: Record<string, number> = {};
  for (const [key, val] of Object.entries(src)) {
    if (typeof val === 'number' && Number.isFinite(val)) out[key] = val;
  }
  return out;
}

export function normalizeHealth(
  raw: WorkspaceHealthData | null | undefined,
): WorkspaceHealthData | null {
  if (!raw) return null;
  const d = safeObject<any>(raw);
  const exec = safeObject<any>(d.executionSummary);
  return {
    ...d,
    executionSummary: {
      ...exec,
      counts: finiteCounts(exec.counts),
      topOverdue: safeArray(exec.topOverdue),
      recentActivity: safeArray(exec.recentActivity),
    },
  } as WorkspaceHealthData;
}

/** Map one settled dashboard request onto data plus an explicit failure. */
export function fromSettled<T, R>(
  result: PromiseSettledResult<T>,
  normalize: (value: T) => R,
): { data: R | null; failed: boolean } {
  if (result.status === 'rejected') return { data: null, failed: true };
  return { data: normalize(result.value), failed: false };
}

export function normalizeSummary(
  raw: WorkspaceSummary | null | undefined,
): WorkspaceSummary | null {
  if (!raw) return null;
  return raw;
}
